// ShotSearch — URL state schema for the shot detail page filters
// (B3 PR4 / B3d).
//
// The grid filters live in the URL so cold loads + back/forward
// nav restore filter state, and so producers can paste a filtered
// view into a comment / message.
//
// Two adapters live here:
//
//   * `validateShotSearch(raw)` — `validateSearch` callback for the
//     TanStack Router route. Receives `Record<string, unknown>`
//     (whatever `routerSearch.parseSearch` produced), returns the
//     typed `ShotSearch` shape with all fields validated/coerced
//     and unknown fields dropped.
//
//   * `filtersToCandidateFilters(filters, shotId)` — translates the
//     URL-shape into the `CandidateFilters` the `useCandidates`
//     hook consumes. The two shapes are deliberately distinct:
//     URL keys are short (`task`, `tag`, `gen`) for human-readable
//     URLs; `CandidateFilters` keys match the backend (`task_id`,
//     `tag`, `generation_number`).
//
// `status` and `exploration_stage` are single-valued because the
// `/candidates` endpoint (B3a) takes single values for each. The
// FilterSidebar UI surfaces them as a single-select dropdown; a
// future B3a follow-up could broaden the backend to accept OR-multi
// and then this schema can widen to `string[]` in lockstep.

import type { components } from '../api/schema';
import type { CandidateFilters } from '../api/queries/candidates';

// `satisfies` links these to the generated schema enums: if the backend
// enum gains/renames a value, this array stops satisfying the schema type
// and typecheck fails here instead of drifting silently.
const CANDIDATE_STATUS_VALUES = [
  'pending',
  'available',
  'selected',
  'approved',
  'rejected',
  'hidden',
] as const satisfies readonly components['schemas']['CandidateStatus'][];
type CandidateStatus = (typeof CANDIDATE_STATUS_VALUES)[number];

const EXPLORATION_STAGE_VALUES = [
  'explore',
  'narrow',
  'lock',
  'temporal',
  'final',
] as const satisfies readonly components['schemas']['ExplorationStage'][];
type ExplorationStage = (typeof EXPLORATION_STAGE_VALUES)[number];

export const CANDIDATE_STATUS_OPTIONS: readonly CandidateStatus[] =
  CANDIDATE_STATUS_VALUES;
export const EXPLORATION_STAGE_OPTIONS: readonly ExplorationStage[] =
  EXPLORATION_STAGE_VALUES;

// URL-shape (short keys, only filter fields).
export interface ShotSearch {
  task?: string;
  tag?: string[];
  require_all_tags?: boolean;
  selected?: boolean;
  status?: CandidateStatus;
  stage?: ExplorationStage;
  gen?: number;
  // Open candidate detail drawer (B4 PR5 / B4e). Strictly a UI-state
  // pointer — not forwarded to `filtersToCandidateFilters` because
  // the grid query doesn't take it. Lives here because URL state is
  // the single source of truth for the drawer (back/forward and
  // link-paste preserve which candidate is open).
  candidate?: string;
  // Open the full-screen Lightbox on this candidate (L1, PRD Addendum
  // 004 §6.1). Same UI-pointer discipline as `candidate`: it drives an
  // overlay, not the candidate query, so it's excluded from
  // `filtersToCandidateFilters` and `hasActiveFilters`.
  lightbox?: string;
}

function asString(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined;
}

function asBool(v: unknown): boolean | undefined {
  if (v === true || v === '1' || v === 'true') return true;
  if (v === false || v === '0' || v === 'false') return false;
  return undefined;
}

function asInt(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return Math.trunc(v);
  if (typeof v === 'string') {
    const n = Number.parseInt(v, 10);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function asStringArray(v: unknown): string[] | undefined {
  if (Array.isArray(v)) {
    const clean = v.filter(
      (x): x is string => typeof x === 'string' && x !== ''
    );
    return clean.length > 0 ? clean : undefined;
  }
  const s = asString(v);
  return s ? [s] : undefined;
}

function asCandidateStatus(v: unknown): CandidateStatus | undefined {
  const s = asString(v);
  return s && (CANDIDATE_STATUS_VALUES as readonly string[]).includes(s)
    ? (s as CandidateStatus)
    : undefined;
}

function asExplorationStage(v: unknown): ExplorationStage | undefined {
  const s = asString(v);
  return s && (EXPLORATION_STAGE_VALUES as readonly string[]).includes(s)
    ? (s as ExplorationStage)
    : undefined;
}

export function validateShotSearch(raw: Record<string, unknown>): ShotSearch {
  // Each field is added conditionally so the resulting object is the
  // minimal shape — that keeps `Object.keys(search).length === 0`
  // honest as a "no filters" check, and matches `exactOptionalPropertyTypes`
  // (which distinguishes absent from `undefined`).
  const out: ShotSearch = {};

  const task = asString(raw['task']);
  if (task !== undefined) out.task = task;

  const tag = asStringArray(raw['tag']);
  if (tag !== undefined) out.tag = tag;

  const requireAll = asBool(raw['require_all_tags']);
  if (requireAll === true) out.require_all_tags = true;

  const selected = asBool(raw['selected']);
  if (selected === true) out.selected = true;

  const status = asCandidateStatus(raw['status']);
  if (status !== undefined) out.status = status;

  const stage = asExplorationStage(raw['stage']);
  if (stage !== undefined) out.stage = stage;

  const gen = asInt(raw['gen']);
  if (gen !== undefined && gen > 0) out.gen = gen;

  const candidate = asString(raw['candidate']);
  if (candidate !== undefined) out.candidate = candidate;

  const lightbox = asString(raw['lightbox']);
  if (lightbox !== undefined) out.lightbox = lightbox;

  return out;
}

// Translate URL-shape → backend-shape. `shotId` is always supplied
// from the route params (it's not a filter the user picks). The
// resulting object is what useCandidates expects.
export function filtersToCandidateFilters(
  filters: ShotSearch,
  shotId: string,
  limit?: number
): CandidateFilters {
  const out: CandidateFilters = { shot_id: shotId };
  if (filters.task !== undefined) out.task_id = filters.task;
  if (filters.tag !== undefined) out.tag = filters.tag;
  if (filters.require_all_tags === true) out.require_all_tags = true;
  if (filters.selected === true) out.is_selected = true;
  if (filters.status !== undefined) out.candidate_status = filters.status;
  if (filters.stage !== undefined) out.exploration_stage = filters.stage;
  if (filters.gen !== undefined) out.generation_number = filters.gen;
  if (limit !== undefined) out.limit = limit;
  return out;
}

// True when the user has applied no filters — useful for empty-state
// copy ("No candidates yet" vs. "No candidates match these filters").
// `candidate` is excluded because it's a UI pointer to an open drawer,
// not a filter that affects the candidate list.
export function hasActiveFilters(filters: ShotSearch): boolean {
  for (const k of Object.keys(filters) as (keyof ShotSearch)[]) {
    if (k === 'candidate' || k === 'lightbox') continue;
    if (filters[k] !== undefined) return true;
  }
  return false;
}

export type { CandidateStatus, ExplorationStage };
export type CandidateOut = components['schemas']['CandidateOut'];
